<template>
  <b-modal
    :active="true"
    :can-cancel="['escape', 'x', 'outside']"
    @close="onClose"
  >
    <div class="content changelog-content">
      <header class="changelog-header">
        <b-icon icon="history" />
        <h2 class="changelog-title">{{ $t('change_log') }}</h2>
      </header>

      <div class="changelog-list" ref="list">
        <template v-if="entries.length">
          <article
            v-for="entry in entries"
            :key="entry._id"
            class="changelog-entry"
          >
            <div class="entry-header">
              <b-icon icon="account-circle" size="is-small" />
              <span class="entry-user">{{ userName(entry) }}</span>
              <span class="entry-action">{{ actionLabel(entry) }}</span>
              <a
                v-if="entityHref(entry)"
                class="entry-entity-link"
                :href="entityHref(entry)"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                {{ entityLabel(entry) }}
              </a>
              <span v-else class="entry-entity-type">
                {{ entityLabel(entry) }}
              </span>
              <span v-if="entry.tool" class="entry-tool">
                {{ toolLabel(entry.tool) }}
              </span>
              <span class="entry-date">{{ formatDate(entry.at) }}</span>
            </div>

            <div v-if="headerText(entry)" class="entry-context">
              <span class="entry-header-part">{{ headerText(entry) }}</span>
            </div>

            <div
              v-for="(change, index) in visibleChanges(entry)"
              :key="`${entry._id}-${index}`"
              class="entry-change"
            >
              <span class="change-field">{{ fieldLabel(change.field) }}</span>
              <span class="change-original">
                <template v-if="hasValue(change.oldValue)">
                  <strong>{{ $t('change_log_modal.original') }}:</strong>
                  {{ change.oldValue }}
                </template>
              </span>
              <span class="change-new">
                <strong>{{ $t('change_log_modal.new') }}:</strong>
                {{ hasValue(change.newValue) ? change.newValue : '—' }}
              </span>
            </div>
          </article>
        </template>

        <div v-else-if="!isLoading" class="changelog-empty">
          <b-icon icon="history" size="is-large" />
          <p>{{ $t('change_log_modal.empty') }}</p>
        </div>

        <div v-if="isLoading" class="changelog-loading">
          <b-skeleton
            v-for="index in 4"
            :key="index"
            height="56px"
            :animated="true"
          />
        </div>
      </div>

      <footer class="footer-buttons">
        <b-button
          v-if="hasMore"
          :label="$t('change_log_modal.load_more')"
          :loading="isLoading"
          @click="loadMore"
        />
        <b-button :label="$t('shared.close')" @click="onClose" />
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { fieldLabels } from '../ConflictDialog/field-labels';
import {
  GET_JOB_CHANGELOG,
  RESET_JOB_CHANGELOG,
} from '../../store/changeLog/actions/actionTypes';
import {
  GET_CHANGELOG_ENTRIES,
  GET_CHANGELOG_HAS_MORE,
} from '../../store/changeLog/getters/getterTypes';

export default {
  name: 'ChangeLogModal',

  props: {
    jobId: {
      type: String,
      required: true,
    },
    tool: {
      type: String,
      default: '',
    },
    // e.g. /view/editor/work-instructions
    libraryRouteBase: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isLoading: false,
    };
  },

  computed: {
    entries() {
      return this.$store.getters[GET_CHANGELOG_ENTRIES];
    },

    hasMore() {
      return this.$store.getters[GET_CHANGELOG_HAS_MORE];
    },

    resolvedTool() {
      if (this.tool) {
        return this.tool;
      }
      const pathTool = this.$route?.path?.split('/')[2];
      return this.$route?.meta?.tool || pathTool || 'editor';
    },
  },

  async mounted() {
    await this.fetchPage(true);
  },

  beforeDestroy() {
    this.$store.dispatch(RESET_JOB_CHANGELOG);
  },

  methods: {
    onClose() {
      this.$emit('onClose');
    },

    async fetchPage(reset) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(GET_JOB_CHANGELOG, {
          jobId: this.jobId,
          reset,
        });
      } catch (error) {
        this.$store.dispatch('setErrorMessage', error.message);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMore() {
      await this.fetchPage(false);
    },

    userName(entry) {
      return (
        (entry.user && entry.user.fullname) ||
        this.$t('change_log_modal.unknown_user')
      );
    },

    actionLabel(entry) {
      const key = `change_log_modal.actions.${entry.action}`;
      const label = this.$t(key);
      return label === key ? this.$t('change_log_modal.actions.update') : label;
    },

    entityLabel(entry) {
      const key = `change_log_modal.entities.${entry.entityType}`;
      const label = this.$t(key);
      return label === key ? entry.entityType : label;
    },

    toolLabel(tool) {
      const key = `change_log_modal.tools.${tool}`;
      const label = this.$t(key);
      return label === key ? tool : label;
    },

    fieldLabel(field) {
      return fieldLabels[field] || field;
    },

    hasValue(value) {
      return value !== null && value !== undefined && value !== '';
    },

    meta(entry) {
      return (entry && entry.meta) || {};
    },

    headerParts(entry) {
      const meta = this.meta(entry);
      const h = this.$t.bind(this);

      if (entry.action === 'reorder' && entry.entityType === 'task') {
        const parts = [];
        if (meta.folderName) {
          parts.push(
            h('change_log_modal.header.project_phase', {
              value: meta.folderName,
            })
          );
        }
        parts.push(
          entry.entityName ||
            h('change_log_modal.header.tasks_reordered', {
              count: meta.reorderCount || 0,
            })
        );
        return parts;
      }

      if (entry.entityType === 'repairAssessment') {
        const parts = [];
        if (meta.isMinor === true || meta.isMinor === false || meta.assembly) {
          const kind = meta.isMinor
            ? h('change_log_modal.header.minor')
            : h('change_log_modal.header.major');
          const assembly = meta.assembly || meta.partName;
          parts.push(
            assembly
              ? h('change_log_modal.header.rat_component', {
                  kind,
                  assembly,
                })
              : kind
          );
        } else if (entry.entityName) {
          parts.push(entry.entityName);
        }
        if (meta.itemNumber) {
          parts.push(
            h('change_log_modal.header.item_number', {
              value: meta.itemNumber,
            })
          );
        }
        return parts;
      }

      if (entry.entityType === 'bubble') {
        return [
          meta.taskId &&
            h('change_log_modal.header.task_id', { value: meta.taskId }),
          meta.bubbleId &&
            h('change_log_modal.header.bubble_id', { value: meta.bubbleId }),
          meta.bubbleName &&
            h('change_log_modal.header.bubble_name', {
              value: meta.bubbleName,
            }),
          meta.bubbleType &&
            h('change_log_modal.header.bubble_type', {
              value: meta.bubbleType,
            }),
        ].filter(Boolean);
      }

      if (entry.entityType === 'question') {
        return [
          meta.folderName &&
            h('change_log_modal.header.project_phase', {
              value: meta.folderName,
            }),
          meta.taskId &&
            h('change_log_modal.header.task_id', { value: meta.taskId }),
          meta.questionId &&
            h('change_log_modal.header.question_id', {
              value: meta.questionId,
            }),
          meta.questionName &&
            h('change_log_modal.header.question_name', {
              value: meta.questionName,
            }),
          meta.questionType &&
            h('change_log_modal.header.question_type', {
              value: meta.questionType,
            }),
        ].filter(Boolean);
      }

      if (
        entry.entityType === 'workInstruction' ||
        entry.entityType === 'workInstructionPackage'
      ) {
        return [
          entry.entityName,
          meta.packageId &&
            h('change_log_modal.header.package_id', {
              value: meta.packageId,
            }),
        ].filter(Boolean);
      }

      if (entry.entityType === 'task' || entry.entityType === 'folder') {
        const parts = [];
        if (meta.folderName) {
          parts.push(
            h('change_log_modal.header.project_phase', {
              value: meta.folderName,
            })
          );
        }
        if (
          entry.entityName &&
          !(entry.action === 'create' && entry.entityName === meta.taskName)
        ) {
          parts.push(entry.entityName);
        } else if (meta.taskName && entry.action !== 'create') {
          parts.push(meta.taskName);
        }
        if (meta.taskId && entry.action !== 'create') {
          parts.push(
            h('change_log_modal.header.task_id', { value: meta.taskId })
          );
        }
        return parts;
      }

      if (entry.entityName) {
        return [entry.entityName];
      }
      return [];
    },

    headerText(entry) {
      return this.headerParts(entry).join(' · ');
    },

    visibleChanges(entry) {
      if (entry.action === 'reorder') {
        return [];
      }
      return (entry.changes || []).filter(
        (change) => change.field !== 'taskOrder'
      );
    },

    entityHref(entry) {
      const { jobId } = this;
      if (!jobId) {
        return null;
      }

      if (jobId.startsWith('library_')) {
        const packageId = jobId.slice('library_'.length);
        if (!this.libraryRouteBase || !packageId) {
          return null;
        }
        return `${this.libraryRouteBase}/${packageId}`;
      }

      const tool = this.resolvedTool;
      if (!tool) {
        return null;
      }

      const meta = this.meta(entry);
      const base = `/view/${tool}/${jobId}`;

      if (entry.entityType === 'repairAssessment') {
        return `${base}?openRat=1`;
      }

      if (
        entry.entityType === 'task' ||
        entry.entityType === 'folder' ||
        entry.entityType === 'bubble' ||
        entry.entityType === 'question'
      ) {
        const taskRef =
          meta.taskId ||
          meta.taskNumber ||
          (entry.entityType === 'task' || entry.entityType === 'folder'
            ? entry.entityId
            : null);
        if (!taskRef || taskRef === jobId) {
          return base;
        }
        return `${base}/${taskRef}`;
      }

      if (
        entry.entityType === 'workInstruction' ||
        entry.entityType === 'workInstructionPackage'
      ) {
        return base;
      }

      if (entry.entityType === 'job') {
        return base;
      }

      return null;
    },

    formatDate(epochMs) {
      if (!epochMs) {
        return '-';
      }
      const date = new Date(epochMs);
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    },
  },
};
</script>

<style scoped>
.changelog-content {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 500px;
  max-height: 80vh;
  font-family: 'Siemens Sans', sans-serif;
}

.changelog-header {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 12px;
}

.changelog-content h2.changelog-title {
  font-size: 18px;
  font-weight: 400;
  margin: 0;
  color: #363636;
}

.changelog-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
}

.changelog-entry {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.changelog-entry:last-child {
  border-bottom: none;
}

.changelog-entry:hover {
  background: #f5f5f5;
}

.entry-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  color: #363636;
  font-size: 14px;
}

.entry-user {
  font-weight: 700;
}

.entry-entity-link {
  color: #0066b3;
  font-weight: 600;
  text-decoration: underline;
}

.entry-entity-type {
  font-weight: 600;
}

.entry-date {
  margin-left: auto;
  color: #666666;
}

.entry-context {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-size: 13px;
  color: #666666;
}

.entry-header-part {
  color: #363636;
}

.entry-tool {
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  padding: 0 6px;
  font-size: 12px;
  text-transform: capitalize;
}

.entry-change {
  display: grid;
  grid-template-columns: 1fr 2fr 2fr;
  gap: 12px;
  align-items: baseline;
  margin-top: 8px;
  font-size: 14px;
  color: #363636;
  overflow-wrap: anywhere;
}

.change-field {
  font-weight: 700;
}

.changelog-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 300px;
  color: #666666;
}

.changelog-loading {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-buttons {
  display: flex;
  justify-content: end;
  gap: 8px;
}

@media (max-width: 768px) {
  .entry-change {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}
</style>
